import {Menu} from "@mescius/wijmo.input";
import {CellRange, CellType, Column, FlexGrid, HitTestInfo} from "@mescius/wijmo.grid";
import {AccountData} from '../../api-client-kt/src';

export interface MenuItem<C> {
    header: string;
    headerHtml?: string;
    iconCls?: string;
    cmd?: C;
    items?: MenuItem<C>[];
    menu?: Menu;
    data?: any;
}

export interface FlexGridContextMenuInfo<T> {
    grid: FlexGrid;
    menu: Menu;
    hitTestInfo: HitTestInfo;
    clickedItem: T;
    clickedColumn: Column | null;
    selectedItems: T[];
    isMultiSelect: boolean;
    isRowHeader: boolean;
}

export class MenuUtils {
    static createMenu<C>(menuItems: MenuItem<C>[]): Menu {
        MenuUtils.expandMenuTree(menuItems);

        const menuHostElement = document.createElement('div');
        const menu = new Menu(menuHostElement, {
                displayMemberPath: 'headerHtml',
                selectedValuePath: 'cmd',
                dropDownCssClass: 'grid-ctx-menu',
                subItemsPath: 'items',
                itemsSource: menuItems,
            }
        );

        // fill in the menu property, to enable menu items to access the menu
        MenuUtils.walkMenuTree(menuItems, item => item.menu = menu);
        return menu;
    }

    static expandMenuTree<C>(menuItems: MenuItem<C>[]): MenuItem<C>[] {
        MenuUtils.walkMenuTree(menuItems, item => {
            if (item.header === '-') {
                item.headerHtml = '<div class="wj-separator"></div>';
            } else if (!item.headerHtml) {
                item.headerHtml = `<i class="menu-icon ${item.iconCls ?? ''}"></i> ${item.header}`;
            }
        });
        return menuItems;
    }

    static walkMenuTree<C>(menuItems: MenuItem<C>[], fn: (item: MenuItem<C>) => void): void {
        for (const item of menuItems) {
            fn(item);
            if (item.items && item.items.length > 0) {
                this.walkMenuTree(item.items, fn);
            }
        }
    }

    static addRowContextMenu(grid: FlexGrid, menu: Menu) {
        grid.hostElement.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            const ht = grid.hitTest(e.pageX, e.pageY);
            switch (ht.cellType) {
                case CellType.ColumnHeader:
                    break;

                case CellType.Cell:
                case CellType.RowHeader:
                    const ctxInfo = MenuUtils.findAndAdjustGridItemsForContextMenu(grid, menu, e);
                    (menu as any).ctxMenuInfo = ctxInfo;
                    menu.owner = grid.hostElement;
                    menu.show(e);
            }
        }, true);
    }

    static getContextMenuInfo<T>(menu: Menu): FlexGridContextMenuInfo<T> {
        const ctxInfo = (menu as any).ctxMenuInfo as FlexGridContextMenuInfo<T>;
        if (!ctxInfo) {
            throw new Error('Context menu info not found. Ensure you have called addRowContextMenu() before accessing context menu info.');
        } else {
            return ctxInfo;
        }
    }

    static findAndAdjustGridItemsForContextMenu(grid: FlexGrid, menu: Menu, e: MouseEvent): FlexGridContextMenuInfo<any> {
        const ht = grid.hitTest(e.pageX, e.pageY);
        const clickedItem = ht.getRow().dataItem;
        const clickedColumn = ht.col >= 0 ? grid.columns[ht.col] : null;
        const selectedItems = grid.selectedItems;
        const isRowHeader = ht.cellType === CellType.RowHeader;

        if (selectedItems.indexOf(clickedItem) >= 0) {
            return {
                grid, menu, isRowHeader, clickedItem, clickedColumn, selectedItems, hitTestInfo: ht, isMultiSelect: selectedItems.length > 1
            };
        } else {
            // if the clicked item is not selected, select it to signal the user what item is being acted upon.
            // if the row header is clicked, select the entire row.
            if (isRowHeader) {
                grid.selection = new CellRange(ht.row, 0, ht.row, grid.columns.length - 1);
            } else {
                grid.selection = new CellRange(ht.row, ht.col, ht.row, ht.col);
            }
            return { grid, menu, isRowHeader, clickedItem, clickedColumn, selectedItems: [clickedItem], hitTestInfo: ht, isMultiSelect: false };
        }
    }
}
